import { Link } from "react-router-dom";
import { Check, Shield, Clock, Award } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { SEO } from "../components/SEO";
import { maintenancePackages } from "../mockData";

export const Maintenance = () => {
  return (
    <div className="min-h-screen bg-navy-900 pt-20">
      <SEO 
        title="Maintenance Contracts | Magic C Plumbing | Plumber & Heating Engineer in London"
        description="Protect your luxury heating and plumbing systems with tailored maintenance contracts. Enjoy 8-12% energy savings, prevent costly breakdowns, and benefit from priority service for homeowners, landlords, and businesses."
        canonical="/maintenance-contracts"
      />
      {/* Hero */}
      <section className="py-24 bg-gradient-to-b from-navy-900 to-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Annual Maintenance Contracts
            </h1>
            <p className="text-2xl text-gray-300 leading-relaxed">
              Protect your investment with comprehensive maintenance packages for residential and commercial clients
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { icon: Shield, title: "Peace of Mind", description: "Regular inspections prevent costly emergencies" },
              { icon: Clock, title: "Priority Service", description: "Skip the queue with priority call-outs" },
              { icon: Award, title: "Cost Savings", description: "Save up to 20% on repairs and parts" }
            ].map((benefit) => {
              const Icon = benefit.icon;
              return (
                <div key={benefit.title} className="text-center">
                  <div className="bg-sage-500/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-md border border-sage-500/30">
                    <Icon className="h-10 w-10 text-sage-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                  <p className="text-gray-300">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="py-24 bg-navy-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Choose Your Package
            </h2>
            <p className="text-xl text-gray-300">
              Flexible maintenance solutions for every need
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10 max-w-6xl mx-auto">
            {maintenancePackages.map((pkg) => (
              <Card 
                key={pkg.id} 
                className={`relative ${
                  pkg.popular 
                    ? 'bg-gradient-to-br from-sage-500 to-sage-600 text-white shadow-2xl transform scale-105 border-4 border-sage-400' 
                    : 'bg-white/5 backdrop-blur-md border-white/10 text-white'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-yellow-400 text-navy-900 px-8 py-3 rounded-full text-sm font-bold shadow-xl">
                    MOST POPULAR
                  </div>
                )}
                <CardContent className="p-10">
                  <div className="text-sm font-bold uppercase tracking-wider mb-3 opacity-90">
                    {pkg.type}
                  </div>
                  <h3 className="text-3xl font-bold mb-6">
                    {pkg.title}
                  </h3>
                  <div className="mb-8">
                    <span className="text-5xl font-bold">{pkg.price}</span>
                    {pkg.price !== "Custom Quote" && <span className="text-xl opacity-80">/year</span>}
                  </div>
                  <ul className="space-y-4 mb-10">
                    {pkg.features.map((feature, idx) => (
                      <li key={`${pkg.name}-${idx}`} className="flex items-start space-x-3">
                        <Check className={`h-6 w-6 flex-shrink-0 mt-0.5 ${pkg.popular ? 'text-white' : 'text-sage-400'}`} />
                        <span className="text-base">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact">
                    <Button 
                      className={`w-full py-6 text-lg ${
                        pkg.popular 
                          ? 'bg-white text-sage-700 hover:bg-gray-100' 
                          : 'bg-sage-600 text-white hover:bg-sage-700'
                      } shadow-xl`}
                    >
                      {pkg.popular ? "Protect Your Investment Today" : "Get Started"}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-16 text-center">
            <p className="text-lg text-gray-300 mb-4">
              All packages include priority customer service and 100% satisfaction guarantee
            </p>
            <a href="tel:03337890987" className="text-sage-300 hover:text-sage-200 font-semibold text-xl">
              Call 0333 789 0987 for custom solutions
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
